import {
	FormProvider,
	useField,
	type FieldMetadata,
	useFormMetadata,
} from "@conform-to/react"
import type * as LabelPrimitive from "@radix-ui/react-label"
import { Slot } from "@radix-ui/react-slot"

import { Form as RemixForm } from "@remix-run/react"
import * as React from "react"
import { cn } from "#app/utils/misc"
import { Label } from "../../label"

const FormContext = ({
	...props
}: React.ComponentProps<typeof FormProvider>) => {
	return <FormProvider {...props}>{props.children}</FormProvider>
}
FormContext.displayName = "FormContext"

const Form = React.forwardRef<
	React.ElementRef<typeof RemixForm>,
	React.ComponentPropsWithoutRef<typeof RemixForm>
>(({ ...formProps }, ref) => {
	return (
		<RemixForm
			ref={ref}
			{...formProps}
			className={cn("flex flex-col gap-4", formProps.className)}
		></RemixForm>
	)
})
Form.displayName = "Form"

type FormFieldContextValue = FieldMetadata & {
	fallbackId: string
	formItemId: string
	formDescriptionId: string
	formMessageId: string
}

const FormFieldContext = React.createContext<FormFieldContextValue>(
	{} as FormFieldContextValue,
)

const useFormField = () => {
	const fieldContext = React.useContext(FormFieldContext)

	return {
		...fieldContext,
	}
}

const FormField = ({
	...props
}: {
	field: FieldMetadata
	children: React.ReactNode
}) => {
	const fallbackId = React.useId()
	const form = useFormMetadata(props.field.formId)
	const id =
		form.id && props.field.name ? `${form.id}-${props.field.name}` : fallbackId
	return (
		<FormFieldContext.Provider
			value={{
				...props.field,
				id,
				name: props.field.name,
				formItemId: `${id}`,
				formDescriptionId: `${id}-form-item-description`,
				formMessageId: `${id}-form-item-message`,
				fallbackId,
			}}
		>
			{props.children}
		</FormFieldContext.Provider>
	)
}

const FormLabel = React.forwardRef<
	React.ElementRef<typeof LabelPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
	const { formItemId, name } = useFormField()
	const [meta] = useField(name)
	const error = Boolean(meta.errors?.length)

	return (
		<Label
			ref={ref}
			className={cn(error && "text-foreground-destructive", className)}
			htmlFor={formItemId}
			{...props}
		/>
	)
})
FormLabel.displayName = "FormLabel"

const FormControl = React.forwardRef<
	React.ElementRef<typeof Slot>,
	React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
	const { id, formItemId, formDescriptionId, formMessageId, name } =
		useFormField()
	const [meta] = useField(name)
	const errorId = meta.errors?.length ? `${id}-error` : undefined

	return (
		<Slot
			ref={ref}
			{...props}
			id={formItemId}
			aria-describedby={
				!errorId
					? `${formDescriptionId}`
					: `${formDescriptionId} ${formMessageId}`
			}
			aria-invalid={errorId ? true : undefined}
		/>
	)
})
FormControl.displayName = "FormControl"

const FormDescription = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
	const { formDescriptionId } = useFormField()

	return (
		<p
			ref={ref}
			id={formDescriptionId}
			className={cn("text-[0.8rem] text-muted-foreground", className)}
			{...props}
		/>
	)
})
FormDescription.displayName = "FormDescription"

const FormMessage = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
	const { formMessageId, name } = useFormField()
	const [meta] = useField(name)

	if (meta.errors?.filter(Boolean)) {
		return (
			<ul id={meta.errorId} className="flex flex-col gap-1">
				{meta.errors.map(e => (
					<li key={e} className="text-[10px] text-foreground-destructive">
						{e}
					</li>
				))}
			</ul>
		)
	}

	if (!children) return null

	return (
		<p
			ref={ref}
			id={formMessageId}
			className={cn(
				"text-[0.8rem] font-medium text-foreground-destructive",
				className,
			)}
			{...props}
		>
			{children}
		</p>
	)
})
FormMessage.displayName = "FormMessage"

const FormErrorList = ({
	formId,
	...props
}: { formId: string } & React.DetailedHTMLProps<
	React.HTMLAttributes<HTMLUListElement>,
	HTMLUListElement
>) => {
	const form = useFormMetadata(formId)
	const errorsToRender = form.errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul {...props} className={cn("flex flex-col gap-1", props.className)}>
			{errorsToRender.map(e => (
				<li key={e} className="text-[10px] text-foreground-destructive">
					{e}
				</li>
			))}
		</ul>
	)
}
FormErrorList.displayName = "FormErrorList"

export {
	Form,
	FormContext,
	FormField,
	useFormField,
	FormLabel,
	FormControl,
	FormMessage,
	FormDescription,
	FormErrorList,
}
